import { roundOffAndFormatNumberWithCurrency } from '../../../../utils/utils'
import {
  HarvestingPlanType,
  type LtcgFinancialPlanResponse,
  type StcgFinancialPlanResponse
} from '../../types/summary.types'

export function getLtcgHighlightTitle({
  hasEquityLtcgPlan,
  hasNonEquityLtcgPlan,
  equityLtcgPlan,
  nonEquityLtcgPlan
}: {
  hasEquityLtcgPlan: boolean
  hasNonEquityLtcgPlan: boolean
  equityLtcgPlan?: LtcgFinancialPlanResponse
  nonEquityLtcgPlan?: LtcgFinancialPlanResponse
}): string {
  if (!hasEquityLtcgPlan && !hasNonEquityLtcgPlan) return ''

  const format = (value: number | undefined) => roundOffAndFormatNumberWithCurrency(value ?? 0)

  if (hasEquityLtcgPlan && equityLtcgPlan) {
    const possibleTypes = equityLtcgPlan.possibleFolioTaxHarvestingTypes
    const isEqLtcgPlan = equityLtcgPlan.folioTaxHarvestingType === HarvestingPlanType.EQ_LTCG

    const shouldShowTaxSavingsForLtcgHighlight =
      isEqLtcgPlan && Array.isArray(possibleTypes) && !possibleTypes.includes(HarvestingPlanType.EQ_LTCG)

    if (shouldShowTaxSavingsForLtcgHighlight || !isEqLtcgPlan) {
      return `<p>Save <span style='font-weight: 600;'> ${format(equityLtcgPlan.possibleTaxSave)} </span> in tax</p>`
    }
    return `<p>Save <span style='font-weight: 600;'> ${format(equityLtcgPlan.possibleTaxSave)} </span> in future tax</p>`
  }

  return `<p>Save <span style='font-weight: 600;'> ${format(nonEquityLtcgPlan?.possibleTaxSave)} </span> in tax</p>`
}

export function getStcgHighlightTitle({
  hasEquityStcgPlan,
  hasNonEquityStcgPlan,
  equityStcgPlan,
  nonEquityStcgPlan
}: {
  hasEquityStcgPlan: boolean
  hasNonEquityStcgPlan: boolean
  equityStcgPlan?: StcgFinancialPlanResponse
  nonEquityStcgPlan?: StcgFinancialPlanResponse
}): string {
  if (!hasEquityStcgPlan && !hasNonEquityStcgPlan) return ''

  const format = (value: number | undefined) => roundOffAndFormatNumberWithCurrency(value ?? 0)
  if (hasEquityStcgPlan) {
    return `<p>Save <span style='font-weight: 600;'> ${format(equityStcgPlan?.possibleTaxSave)} </span> in tax</p>`
  }

  return `<p>Save <span style='font-weight: 600;'> ${format(nonEquityStcgPlan?.possibleTaxSave)} </span> in tax</p>`
}

export function getLtcgHighlightSubtitle(
  hasEquityLtcgPlan: boolean,
  gainBookAmount: number | undefined,
  equityLtcgPlan?: LtcgFinancialPlanResponse
): string {
  const format = (value: number | undefined) => roundOffAndFormatNumberWithCurrency(value ?? 0)

  if (hasEquityLtcgPlan && equityLtcgPlan) {
    const possibleTypes = equityLtcgPlan.possibleFolioTaxHarvestingTypes
    const hasEqLtcgOption = Array.isArray(possibleTypes) && possibleTypes.includes(HarvestingPlanType.EQ_LTCG)

    if (hasEqLtcgOption) {
      return `by booking ${format(gainBookAmount)} gains`
    }
    return `by booking ${format(gainBookAmount)} losses`
  }
  return `by booking ${format(gainBookAmount)} losses`
}

export function getStcgHighlightSubtitle(loss: number): string {
  return `by booking ${roundOffAndFormatNumberWithCurrency(loss)} losses`
}
